import { useQuota } from '../hooks/useSubscription';
import { AlertCircle } from 'lucide-react';

interface UsageQuotaDisplayProps {
  quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs';
  title: string;
  description?: string;
}

export function UsageQuotaDisplay({ quotaType, title, description }: UsageQuotaDisplayProps) {
  const { quota, loading, isLimitReached, percentageUsed } = useQuota(quotaType);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (!quota || !quota.exists) {
    return null;
  }

  const getProgressColor = () => {
    if (percentageUsed >= 90) return 'bg-red-600';
    if (percentageUsed >= 75) return 'bg-yellow-600';
    return 'bg-blue-600';
  };

  const getTextColor = () => {
    if (percentageUsed >= 90) return 'text-red-600';
    if (percentageUsed >= 75) return 'text-yellow-600';
    return 'text-blue-600';
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-600">{description}</p>}
        </div>
        {isLimitReached && (
          <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            {quota.used} of {quota.limit} used
          </span>
          <span className={`font-semibold ${getTextColor()}`}>
            {quota.remaining} remaining
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${getProgressColor()}`}
            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
          ></div>
        </div>

        {isLimitReached && (
          <div className="mt-2 text-sm text-red-600 font-medium">
            You've reached your monthly limit. Upgrade to continue.
          </div>
        )}

        {quota.resets_at && (
          <div className="text-xs text-gray-500">
            Resets on {new Date(quota.resets_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}
